import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Bell, FileSignature, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import { MY_COMPANY_CONTRACT_STATUS } from "@graphql/queries/contracts";
import { ACKNOWLEDGE_CONTRACT } from "@graphql/mutations/contracts";
import { getReadableErrorMessage } from "@pages/users/form/formHelpers";
import ContractForm from "./ContractForm";
import type { ContractField } from "./buildContractSchema";

interface PendingObligation {
  versionId: string;
  typeSlug: string;
  typeName: string;
  title: string;
  acknowledgementMode: string;
  isBlocking: boolean;
  content: string;
  fields: ContractField[];
  countries: string[];
  prefill: Record<string, unknown> | null;
}

/** Form contracts first, so the customer clears what actually locks them out. */
const sortObligations = (items: PendingObligation[]): PendingObligation[] =>
  [...items].sort((a, b) => Number(b.isBlocking) - Number(a.isBlocking));

function NoticeBody({
  obligation,
  onConfirm,
  submitting,
}: {
  obligation: PendingObligation;
  onConfirm: () => void;
  submitting: boolean;
}) {
  return (
    <div className="space-y-4">
      <div
        className="max-h-[50vh] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 [&_a]:text-brand-primary [&_a]:underline [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-3 [&_table]:w-full [&_td]:border [&_td]:border-slate-200 [&_td]:p-1.5 [&_th]:border [&_th]:border-slate-200 [&_th]:p-1.5"
        // The document is authored by the Super Admin in the contract
        // management screen, not by customers, so it is trusted markup.
        dangerouslySetInnerHTML={{ __html: obligation.content }}
      />
      <Button
        style="primary"
        className="w-full"
        disabled={submitting}
        onClick={onConfirm}
      >
        <div className="flex items-center justify-center gap-1.5">
          <span>Zur Kenntnis genommen</span>
          {submitting && <Loader2 className="h-4 w-4 animate-spin text-white" />}
        </div>
      </Button>
    </div>
  );
}

const ContractGateModal = () => {
  const [noticeSubmitting, setNoticeSubmitting] = useState(false);
  const { data, loading, refetch } = useQuery(MY_COMPANY_CONTRACT_STATUS, {
    fetchPolicy: "cache-and-network",
  });
  const [acknowledgeContract] = useMutation(ACKNOWLEDGE_CONTRACT);

  const status = data?.myCompanyContractStatus;
  const pending = useMemo(
    () => sortObligations((status?.pending ?? []) as PendingObligation[]),
    [status],
  );

  // One dialog at a time: the customer works through the queue in order.
  const current = pending[0] ?? null;

  if (loading || !status || current === null) return null;

  const isNotice = current.acknowledgementMode === "notice";

  const submit = async (values: Record<string, string>) => {
    await acknowledgeContract({
      variables: { versionId: current.versionId, data: values },
    });
    await refetch();
  };

  const confirmNotice = async () => {
    setNoticeSubmitting(true);
    try {
      await submit({});
    } catch (err: unknown) {
      toast.error(
        getReadableErrorMessage(
          err,
          "Die Bestätigung konnte nicht gespeichert werden.",
        ),
      );
    } finally {
      setNoticeSubmitting(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        hideCloseButton={current.isBlocking}
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        onEscapeKeyDown={(e) => current.isBlocking && e.preventDefault()}
        onPointerDownOutside={(e) => current.isBlocking && e.preventDefault()}
        onInteractOutside={(e) => current.isBlocking && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNotice ? (
              <Bell className="h-5 w-5 text-brand-primary" />
            ) : (
              <FileSignature className="h-5 w-5 text-brand-primary" />
            )}
            {current.title}
          </DialogTitle>
          <DialogDescription>
            {isNotice ? (
              <>Bitte lesen Sie den folgenden Hinweis und bestätigen Sie ihn.</>
            ) : (
              <>
                Bitte füllen Sie die folgenden Angaben aus, um „
                {current.typeName}" abzuschließen.
                {current.isBlocking && (
                  <>
                    {" "}
                    Ohne diese Angabe können Sie die Anwendung nicht weiter
                    nutzen.
                  </>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {pending.length > 1 && (
          <p className="text-xs text-slate-500">
            {pending.length === 2
              ? "Im Anschluss ist noch eine weitere Bestätigung offen."
              : `Im Anschluss sind noch ${pending.length - 1} weitere Bestätigungen offen.`}
          </p>
        )}

        {isNotice ? (
          <NoticeBody
            obligation={current}
            onConfirm={confirmNotice}
            submitting={noticeSubmitting}
          />
        ) : (
          <ContractForm
            // Remount the form when the queue advances, so the next contract
            // never inherits the previous one's field values.
            key={current.versionId}
            fields={current.fields}
            countries={current.countries}
            prefill={current.prefill}
            deliveryEmail={status.deliveryEmail}
            submitLabel={`${current.typeName} abschließen`}
            onSubmit={submit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractGateModal;
